export const docsSectionToPath = {
  platformOverview: 'platform-overview',
  gettingStarted: 'getting-started',
  conversationTasks: 'conversation-tasks',
  projectsKnowledge: 'projects-knowledge',
  skillsNodes: 'skills-nodes',
  collaborationControl: 'collaboration-control',
  webProjects: 'web-digital-projects',
  creativeProduction: 'creative-production',
  remoteOps: 'remote-operations',
  desktopExecution: 'desktop-execution',
  mobileNodes: 'mobile-node-workflows',
  teamWorkflow: 'team-workflows',
  suitableTasks: 'suitable-tasks',
  whatIsNode: 'what-is-a-node',
  projectKnowledgeDiff: 'project-vs-knowledge',
  whyNotChat: 'why-not-chat-product',
  capabilityBoundaries: 'capability-boundaries'
}

// Docs content uses user-facing product names rather than internal route paths.
export const docsContent = {
  zh: {
    defaultSection: 'platformOverview',
    pageTitle: '文档中心',
    pageSubtitle: '按真实产品入口理解 Avelia：从私人助理发起，到项目、知识、节点与创意协同落地。',
    menuTitle: '文档目录',
    searchPlaceholder: '搜索私人助理、项目管理、知识库、节点管理、创意工坊...',
    searchEmpty: '没有找到匹配内容，请尝试更贴近任务目标或产品入口的关键词。',
    comingSoon: '文档内容正在整理中。',
    copyButtonLabel: '复制当前文档',
    copySuccessLabel: '已复制',
    copyErrorLabel: '复制失败',
    copyButtonHint: '复制当前页面内容，方便转给团队成员，或继续让 Avelia 沿着同一目标推进。',
    usageTipTitle: '阅读建议',
    usageTipContent:
      '先读“功能”，知道该从哪个入口开始；再读“场景”，把这套路径套进你手头的真实任务。',
    sourceLabel: '来源',
    categories: [
      {
        id: 'features',
        title: '功能',
        items: [
          { id: 'platformOverview', name: '平台是什么', desc: '先理解几个核心入口如何串成一条工作链' },
          { id: 'gettingStarted', name: '如何开始使用', desc: '第一次进入后，怎样更快拿到首个可执行结果' },
          { id: 'conversationTasks', name: '会话与任务', desc: '在私人助理里发起目标、挂附件、选技能、看进展' },
          { id: 'projectsKnowledge', name: '项目与知识', desc: '判断何时进入项目管理，何时把资料交给知识库' },
          { id: 'skillsNodes', name: '技能与节点', desc: '理解技能调用、节点接入与执行分配的关系' },
          { id: 'collaborationControl', name: '协作与控制', desc: '在私人助理、项目管理和群组协作之间选对推进方式' }
        ]
      },
      {
        id: 'scenarios',
        title: '场景',
        items: [
          { id: 'webProjects', name: '官网与数字项目', desc: '适合官网改版、文档站点、落地页和长期迭代项目' },
          { id: 'creativeProduction', name: '内容生产', desc: '把图片、视频、音频任务做成可复用的内容资产' },
          { id: 'remoteOps', name: '远程运维', desc: '围绕 Linux 节点处理服务器、部署、数据库与缓存任务' },
          { id: 'desktopExecution', name: '桌面执行', desc: '把本机观察、应用操作与结果回传放进同一链路' },
          { id: 'mobileNodes', name: '移动端节点', desc: '让移动设备参与观察、执行与结果回流' },
          { id: 'teamWorkflow', name: '团队工作流', desc: '面向负责人、成员和长期协作的推进方式' }
        ]
      },
      {
        id: 'faq',
        title: 'FAQ',
        items: [
          { id: 'suitableTasks', name: '什么任务最适合现在开始', desc: '快速判断哪类目标最容易在 Avelia 上看到价值' },
          { id: 'whatIsNode', name: '节点如何参与工作', desc: '理解节点在工作链里承担什么角色' },
          { id: 'projectKnowledgeDiff', name: '项目管理与知识库怎么分工', desc: '明确两者各自承接什么内容' },
          { id: 'whyNotChat', name: '为什么适合持续工作', desc: '理解 Avelia 相比一次性对话工具的关键差异' },
          { id: 'capabilityBoundaries', name: '哪些能力可以立即开始', desc: '知道哪些方向现在就能用，哪些需要前置条件' }
        ]
      }
    ],
    content: {
      platformOverview: {
        title: '如何快速建立正确用法',
        summary:
          'Avelia 最适合的使用方式，不是先研究所有页面，而是先在私人助理里把目标说清，再根据任务性质切到项目管理、知识库、创意工坊或节点管理。',
        sections: [
          {
            subtitle: '先记住这条主线',
            content:
              '先提出目标，再补充资料，再决定是否需要长期项目、创意生成或真实环境执行。顺着这条主线使用，Avelia 才会从一次请求变成一条可回看的工作链。'
          },
          {
            subtitle: '推荐使用顺序',
            steps: [
              '先在私人助理里说清目标、背景、限制和交付结果。',
              '如果任务会持续几轮以上，尽快切到项目管理，让会话、工作区和预览留在同一个容器里。',
              '如果任务依赖资料、规范或历史内容，把这些材料放进知识库，避免每次重新解释。',
              '如果任务需要图片、视频或音频产出，直接进入创意工坊处理生成与复用。',
              '如果任务需要进入桌面、服务器或移动设备执行，再确认节点管理中的节点状态与能力。'
            ]
          },
          {
            subtitle: '六个核心入口怎么分工',
            cards: [
              { title: '私人助理', desc: '默认起点。适合先把目标讲清、补上下文、挂附件、选技能、看任务进展。' },
              { title: '项目管理', desc: '承接长期任务。适合把会话、工作区、工具、预览和交付说明放在一起推进。' },
              { title: '知识库', desc: '承接长期资料。适合上传文档、整理文本、搜索内容，并为后续任务提供可复用背景。' },
              { title: '创意工坊', desc: '承接图片、视频、音频任务，支持参考素材、重试、分享、下载和结果沉淀。' },
              { title: '节点管理', desc: '查看节点接入、在线状态、能力清单和运行指标，决定任务是否能进入真实环境执行。' },
              { title: '设置', desc: '处理模型、工具与接入配置。它是补充入口，不建议作为第一次使用的主路径。' }
            ]
          },
          {
            subtitle: '什么时候算真正开始工作',
            bullets: [
              '你能看到系统已经理解目标，而不是只给出泛泛回答。',
              '任务有了下一步动作、所需材料和执行方向。',
              '需要长期推进时，相关内容已经进入项目管理或知识库。',
              '需要真实执行时，节点状态、能力和权限边界已经明确。'
            ]
          }
        ]
      },
      gettingStarted: {
        title: '如何开始使用',
        summary:
          '第一次进入 Avelia，最重要的不是看完所有页面，而是尽快跑通一条从目标提出到结果回流的真实链路。',
        sections: [
          {
            subtitle: '第一次进入后的推荐路径',
            steps: [
              '登录后先进入私人助理，不必急着切换到其他页面。',
              '用一条完整消息说明目标、背景、时间要求和希望交付的结果。',
              '如果有文件、截图、文档或参考素材，优先一起带上，减少往返追问。',
              '判断这件事是否会持续推进；如果会，尽快建立对应项目，把后续动作放进项目管理。',
              '如果要依赖品牌资料、规范、历史结论或文档内容，把资料先整理进知识库。',
              '如果任务需要本机、服务器或移动设备参与，确认节点已经接入并且状态正常。'
            ]
          },
          {
            subtitle: '第一条消息建议包含什么',
            cards: [
              { title: '目标', desc: '你到底要完成什么，例如改官网首屏、排查部署问题、整理一组资料。' },
              { title: '背景', desc: '当前项目、已有文件、相关系统、团队分工或前置条件。' },
              { title: '交付', desc: '最终想拿到页面、代码、报告、素材，还是一条能复用的处理流程。' },
              { title: '边界', desc: '哪些内容不能改、哪些操作要确认、哪些环境暂时不能进入。' }
            ]
          },
          {
            subtitle: '什么时候切换到其他入口',
            bullets: [
              '需要文件、预览和会话一起长期推进时，切到项目管理。',
              '需要整理文档、沉淀规范、做长期检索时，切到知识库。',
              '需要图片、视频或音频产出时，切到创意工坊。',
              '需要进入桌面、Linux 或移动设备执行时，先看节点管理。',
              '需要配置模型、接入方式或工具权限时，再进入设置。'
            ]
          },
          {
            subtitle: '新手最容易受益的使用习惯',
            callout: {
              tone: 'info',
              title: '先打通一条链，再追求复杂度',
              content:
                '第一轮最理想的结果，不是一次性做完所有事，而是确认目标能被理解、任务能被推进、结果能回到项目或知识层。链路跑通后，再逐步增加节点、技能和协作复杂度。'
            }
          }
        ]
      },
      conversationTasks: {
        title: '会话与任务',
        summary:
          '私人助理是默认入口。它不仅负责对话，还承接目标表达、附件挂载、技能选择、节点偏好和任务过程回传。',
        sections: [
          {
            subtitle: '在私人助理里的一次标准推进',
            steps: [
              '创建或切换到当前最合适的会话。',
              '输入目标和上下文，必要时追加附件或参考材料。',
              '按需要选择技能，或为当前会话指定偏好的执行节点。',
              '观察任务进展、处理中状态、结果回传与阻塞信息。',
              '当任务需要长期推进时，把它迁移到项目管理继续承接。'
            ]
          },
          {
            subtitle: '你能直接感知到的几个关键能力',
            cards: [
              { title: '会话切换', desc: '适合同时推进多个目标，把不同主题分开承接，避免上下文互相干扰。' },
              { title: '附件与参考', desc: '可以补充文件、截图或其他参考材料，减少任务推进中的反复解释。' },
              { title: '技能选择', desc: '当你知道当前任务需要某类能力时，可以直接挂到本轮会话上。' },
              { title: '节点偏好', desc: '当任务明确依赖某台设备或某个环境时，可以指定更合适的执行面。' }
            ]
          },
          {
            subtitle: '什么时候应该从私人助理切出去',
            bullets: [
              '要长期修改文件、看预览、保存工作区时，进入项目管理。',
              '要整理资料、维护文档、做长期检索时，进入知识库。',
              '要生产图片、视频、音频时，进入创意工坊。',
              '要确认执行环境、查看节点能力或绑定新设备时，进入节点管理。'
            ]
          },
          {
            subtitle: '哪些信息越早说明越省时间',
            callout: {
              tone: 'warning',
              title: '把验收标准和权限边界说在前面',
              content:
                '如果任务涉及改代码、进服务器、动数据或执行高风险操作，越早说明交付标准、可访问范围和需要确认的节点，后续返工就越少。'
            }
          }
        ]
      },
      projectsKnowledge: {
        title: '项目与知识',
        summary:
          '项目管理负责把一项工作持续推进下去，知识库负责把资料和结论长期沉淀下来。两者经常一起使用，但分工不同。',
        sections: [
          {
            subtitle: '什么任务应该先进项目管理',
            bullets: [
              '需要多轮迭代、文件改动、页面预览或结果交付的任务。',
              '需要把会话、工作区、工具和说明放在同一容器里的任务。',
              '需要团队成员围绕同一目标持续推进的任务。'
            ]
          },
          {
            subtitle: '项目管理和知识库各自负责什么',
            cards: [
              { title: '项目管理', desc: '承接长期会话、工作区文件、工具配置、预览结果和阶段性交付。' },
              { title: '知识库', desc: '承接品牌资料、产品文档、规范、历史结论和后续检索所需的背景。' },
              { title: '一起配合', desc: '项目负责把结果做出来，知识库负责让下次更容易把结果做出来。' }
            ]
          },
          {
            subtitle: '一条推荐配合方式',
            steps: [
              '先在私人助理中明确目标，判断是否需要长期推进。',
              '需要长期推进时，建立项目并把主会话放进项目管理。',
              '把规范、品牌资料、需求文档等背景内容补进知识库。',
              '在项目中继续修改文件、查看预览、推进交付。',
              '把最终结论、模板和可复用资料回流到知识库。'
            ]
          },
          {
            subtitle: '两个常用判断',
            bullets: [
              '如果重点是“把事情做成”，优先进入项目管理。',
              '如果重点是“把资料留下来并方便后续复用”，优先进入知识库。',
              '如果两者都重要，就让项目管理承接当前推进，让知识库承接长期背景。'
            ]
          }
        ]
      },
      skillsNodes: {
        title: '技能与节点',
        summary:
          '技能决定本轮任务可以调动哪些能力，节点决定这些能力能不能进入真实环境执行。真正有价值的是两者在同一条任务链里协同。',
        sections: [
          {
            subtitle: '节点接入后先看什么',
            steps: [
              '先在节点管理里确认节点是否已经绑定完成。',
              '查看节点当前状态，确认它是在线、忙碌还是暂时不可用。',
              '打开节点详情，确认能力清单、平台信息和最新运行指标。',
              '确认当前任务需要的能力是否已经上报，再回到私人助理或项目管理中发起执行。'
            ]
          },
          {
            subtitle: '什么时候自动分配，什么时候手动指定',
            cards: [
              { title: '自动分配', desc: '适合目标清晰但不依赖某台固定设备时，让系统按可用能力做分配。' },
              { title: '手动指定', desc: '适合你明确知道要用某台 Windows、Linux 或移动设备节点处理当前任务。' },
              { title: '先查再发起', desc: '当你不确定节点能力是否匹配时，先到节点管理看能力清单和运行状态。' }
            ]
          },
          {
            subtitle: '三类节点更适合哪些工作',
            cards: [
              { title: 'Windows 节点', desc: '适合桌面观察、应用操作、工作区协作和本机问题排查。' },
              { title: 'Linux 节点', desc: '适合远程环境、命令执行、部署检查、日志核对和基础设施协作。' },
              { title: 'Android 节点', desc: '适合移动界面观察、流程执行和跨端任务推进。' }
            ]
          },
          {
            subtitle: '让执行更顺利的注意事项',
            bullets: [
              '执行前说明目标系统、权限边界和期望结果，比只说“帮我处理一下”更有效。',
              '涉及服务器、数据库或缓存的任务，先确认节点能力和访问条件再发起。',
              '遇到高风险操作时，明确哪些步骤需要人工确认，哪些可以直接推进。'
            ]
          }
        ]
      },
      collaborationControl: {
        title: '协作与控制',
        summary:
          'Avelia 的协作价值，不在于让更多角色同时发言，而在于把目标、分工、执行和回流组织在同一条可控链路里。',
        sections: [
          {
            subtitle: '三个协作入口怎么选',
            cards: [
              { title: '私人助理', desc: '适合先把目标讲清、快速试跑一条任务链，确认方向后再扩展。' },
              { title: '项目管理', desc: '适合围绕同一个长期目标持续推进，把会话、工作区和预览统一管理。' },
              { title: '群组协作', desc: '适合需要负责人、成员、讨论与协同推进并存的复杂任务。' }
            ]
          },
          {
            subtitle: '高风险任务的推进顺序',
            steps: [
              '先把目标、范围和交付标准说明清楚。',
              '再决定由谁负责判断、谁负责执行、谁负责复核。',
              '进入真实环境前，明确哪些步骤需要人工确认。',
              '执行后的关键结果回流到项目管理或知识库，方便复核和复用。'
            ]
          },
          {
            subtitle: '什么任务更适合团队推进',
            bullets: [
              '需要多阶段交付的任务，例如先分析、再实现、再验证、再整理结果。',
              '需要产品、内容、开发或运维多人连续参与的任务。',
              '需要权限把控、阶段确认或长期沉淀的高价值任务。'
            ]
          },
          {
            subtitle: '结果最终要回到哪里',
            content:
              '无论任务是在私人助理、项目管理还是群组协作里发起，真正有价值的结果都应该回到项目管理或知识库里。这样下一轮推进时，团队不需要从零重新解释。'
          }
        ]
      },
      webProjects: {
        title: '官网与数字项目',
        summary:
          '官网改版、落地页、文档站点和长期数字项目，最适合使用“私人助理 + 项目管理 + 知识库”的组合来推进。',
        sections: [
          {
            subtitle: '这类任务通常包含什么',
            bullets: [
              '文案重写、结构调整和首屏表达优化。',
              '样式修正、响应式调整和视觉统一。',
              '页面预览、版本迭代和交付说明整理。',
              '文档站点维护与品牌资料复用。'
            ]
          },
          {
            subtitle: '推荐任务链',
            steps: [
              '先在私人助理里说明目标、页面范围、风格方向和验收标准。',
              '建立项目，把主会话、工作区和后续迭代都放进项目管理。',
              '将品牌资料、旧文案、规范和截图补进知识库，减少反复解释。',
              '在项目中读取文件、修改页面、查看预览、继续迭代。',
              '把最终页面说明、版本结果和可复用表达沉淀回项目管理或知识库。'
            ]
          },
          {
            subtitle: '推进中最有价值的两个习惯',
            bullets: [
              '把品牌表达和历史资料先放进知识库，再开始改页面，会明显减少来回对齐成本。',
              '每次关键迭代都在项目里留下预览和说明，后续回看会比只看聊天记录高效得多。'
            ]
          }
        ]
      },
      creativeProduction: {
        title: '内容生产',
        summary:
          '创意工坊适合承接图片、视频和音频任务，但真正的价值不只是生成一次结果，而是把参考素材、参数和最终内容做成可复用资产。',
        sections: [
          {
            subtitle: '适合交给创意工坊的方向',
            cards: [
              { title: '图片任务', desc: '品牌视觉、概念图、物料图、场景图和参考图延展。' },
              { title: '视频任务', desc: '短视频片段、镜头延展、参考图驱动视频和连续迭代。' },
              { title: '音频任务', desc: '语音、配乐、提示音和其他需要反复比较的声音内容。' }
            ]
          },
          {
            subtitle: '推荐任务链',
            steps: [
              '先明确目标受众、风格方向和最终用途。',
              '进入创意工坊，选择对应的图片、视频或音频生成方向。',
              '按需要挂参考素材，并记录本轮效果最好的提示词和参数。',
              '对结果进行重试、对比、分享和下载，而不是只保留单次产出。',
              '把最终版本、命名和复用逻辑沉淀到项目管理或知识库。'
            ]
          },
          {
            subtitle: '让内容资产更容易复用',
            bullets: [
              '把成功提示词、参考图和用途说明一起保存，下一次生成质量会更稳定。',
              '需要多人共用素材时，优先在项目管理或知识库中保留最终版本和说明。',
              '当同一主题要持续产出时，保持命名和风格基线一致，会明显提高后续效率。'
            ]
          }
        ]
      },
      remoteOps: {
        title: '远程运维',
        summary:
          '当任务需要进入服务器、数据库、缓存或远程项目环境时，Linux 节点会成为最有价值的执行面。它适合把理解、命令执行和结果回传放进同一条链路。',
        sections: [
          {
            subtitle: '高频需求场景',
            cards: [
              { title: '服务状态排查', desc: '检查服务是否在线、资源是否异常、日志是否出现关键报错。' },
              { title: '部署前后检查', desc: '核对配置、发布结果、进程状态、端口和关键依赖。' },
              { title: '数据库与缓存协助', desc: '处理查询辅助、状态确认、连接诊断和巡检类任务。' },
              { title: '日志与配置核对', desc: '围绕远程项目环境比对配置、查看日志和确认环境差异。' }
            ]
          },
          {
            subtitle: '推荐任务链',
            steps: [
              '先在私人助理或项目管理里说明目标环境、问题现象和期望结果。',
              '确认当前 Linux 节点已经在线，且具备进入目标环境所需的能力与权限。',
              '优先让系统先看状态、查信息、做判断，再进入更大范围的执行动作。',
              '需要持续排查时，把这次任务放进项目管理，保留命令链、结论和后续动作。',
              '将故障原因、处理建议和后续巡检要点沉淀到知识库。'
            ]
          },
          {
            subtitle: '开始前要确认的前置条件',
            bullets: [
              '当前节点能否访问目标主机、数据库、缓存或相关服务。',
              '哪些命令、查询或改动可以直接执行，哪些需要人工确认。',
              '你希望拿到的是状态判断、修复建议，还是直接进入处理。'
            ]
          },
          {
            subtitle: '为什么这类任务适合 Avelia',
            content:
              '远程运维任务往往既需要判断，也需要进入真实环境执行，还需要把结果留下来给下一次复用。Avelia 的优势，就是把这三件事放在同一条可追踪链路里。'
          }
        ]
      },
      desktopExecution: {
        title: '桌面执行',
        summary:
          '当任务需要观察本机状态、操作应用、查看窗口或围绕桌面环境推进时，桌面执行可以把本机观察与任务回流连在一起。',
        sections: [
          {
            subtitle: '适合的任务方向',
            bullets: [
              '本机软件状态检查与问题排查。',
              '围绕窗口、截图、文件和应用的观察型任务。',
              '重复桌面动作的辅助执行与半自动流程。',
              '日常办公、支持和本机协作类任务。'
            ]
          },
          {
            subtitle: '推荐使用方式',
            steps: [
              '先说明当前设备环境、目标软件和希望完成的结果。',
              '把需要确认的动作和可以自动推进的动作提前区分清楚。',
              '让系统先从当前界面、文件或状态开始观察，再决定下一步执行。',
              '把结论、截图、处理步骤或后续建议回流到项目管理或知识库。'
            ]
          },
          {
            subtitle: '桌面执行最值得用在什么地方',
            content:
              '它最有价值的地方，不是单纯代替点击，而是能把“看见当前状态”“决定下一步动作”“把结果带回任务链”放在同一次推进中完成。'
          }
        ]
      },
      mobileNodes: {
        title: '移动端节点',
        summary:
          '移动端节点适合让手机或其他移动设备进入同一条工作链，承担界面观察、流程执行和跨端回流这三类任务。',
        sections: [
          {
            subtitle: '更适合的任务类型',
            cards: [
              { title: '界面观察', desc: '读取当前移动界面状态、元素变化和可能的下一步动作。' },
              { title: '流程执行', desc: '适合点击、输入、滑动、滚动等连续操作型流程。' },
              { title: '跨端协同', desc: '让移动设备与桌面、Linux 节点一起参与同一个任务目标。' }
            ]
          },
          {
            subtitle: '推荐任务链',
            steps: [
              '先说明要处理的移动端目标、应用场景和最终结果。',
              '确认移动端节点已经接入，并具备当前任务所需的能力。',
              '先观察界面状态，再逐步推进具体动作。',
              '把执行过程中的关键状态和结果回流到项目管理或知识库。'
            ]
          },
          {
            subtitle: '开始前的前置条件',
            bullets: [
              '你已经知道要处理的是哪一台设备、哪一段流程、哪类结果。',
              '该设备的节点权限和执行策略已经准备好。',
              '需要回看的结果，例如截图、流程结论或操作说明，有明确沉淀位置。'
            ]
          }
        ]
      },
      teamWorkflow: {
        title: '团队工作流',
        summary:
          '当任务需要负责人、成员、资料、节点和阶段性交付一起配合时，Avelia 更适合把它组织成一条团队工作流，而不是拆成互相脱节的零散对话。',
        sections: [
          {
            subtitle: '这类工作流常见在哪些任务里',
            cards: [
              { title: '发布与改版协作', desc: '例如官网重构、专题页上线、内容与开发并行推进。' },
              { title: '运维与发布保障', desc: '例如部署检查、服务巡检、问题回放和责任分工。' },
              { title: '长期项目推进', desc: '例如持续内容生产、文档维护和跨周期项目协作。' }
            ]
          },
          {
            subtitle: '推荐推进链',
            steps: [
              '由负责人先定义目标、范围、时间要求和验收标准。',
              '把长期任务放进项目管理，确定当前主会话和工作容器。',
              '把规范、资料和历史结论放进知识库，形成全员共享背景。',
              '需要执行时，按任务需要调动桌面、Linux 或移动端节点。',
              '阶段性结果回到项目管理，长期复用内容回到知识库。'
            ]
          },
          {
            subtitle: '让协作稳定下来的几个习惯',
            bullets: [
              '负责人只改目标和边界，不让执行链频繁失焦。',
              '关键节点结果都回流到项目管理，而不是只留在聊天记录里。',
              '重复会用到的模板、经验和检查项尽量沉淀到知识库。',
              '高风险动作保留确认点，让执行和复核可以分开进行。'
            ]
          }
        ]
      },
      suitableTasks: {
        title: '什么任务最适合现在开始',
        summary:
          'Avelia 最容易体现价值的任务，不是“一问一答就结束”的问题，而是那些需要上下文、执行面和结果沉淀的目标。',
        sections: [
          {
            subtitle: '优先尝试这些任务',
            bullets: [
              '要持续推进几轮以上的任务，例如官网改版、资料整理、部署排查。',
              '需要文件、预览、日志、截图或素材一起参与的任务。',
              '需要桌面、Linux 或移动设备进入真实环境执行的任务。',
              '希望把过程和结果沉淀下来，供下一次继续复用的任务。'
            ]
          },
          {
            subtitle: '一上来就能感到价值的信号',
            bullets: [
              '你不需要每次重新解释背景，系统也能继续推进。',
              '结果不只是一段回答，而是会进入项目管理、知识库或创意资产里。',
              '任务需要真实执行时，系统能配合节点进入环境，而不是只给建议。'
            ]
          }
        ]
      },
      whatIsNode: {
        title: '节点如何参与工作',
        summary:
          '节点的价值，在于把 Avelia 带入桌面、Linux 环境和移动设备等真实执行面，让任务不只停留在理解层。',
        sections: [
          {
            subtitle: '节点在工作链里负责什么',
            bullets: [
              '作为真实环境的接入点，把设备能力汇报给平台。',
              '在需要时接收任务调用，并进入目标环境执行。',
              '把状态、进度和结果回流给当前会话、项目管理或知识库。'
            ]
          },
          {
            subtitle: '用户最常感知到的三个价值',
            cards: [
              { title: '能进入环境', desc: '不再只停留在“应该怎么做”，而是能实际处理桌面、服务器或移动设备任务。' },
              { title: '能看状态', desc: '你可以在节点管理里看到在线状态、能力清单和运行指标。' },
              { title: '能留结果', desc: '执行后的结论不会散掉，而是能回到项目管理或知识库继续复用。' }
            ]
          }
        ]
      },
      projectKnowledgeDiff: {
        title: '项目管理与知识库怎么分工',
        summary:
          '两者经常一起使用，但承担的职责不同。理解好分工，任务推进和资料沉淀都会更顺。',
        sections: [
          {
            subtitle: '最短区分',
            cards: [
              { title: '项目管理', desc: '回答的是“这件事怎么继续往前做”。' },
              { title: '知识库', desc: '回答的是“哪些资料和结论要长期留下来”。' }
            ]
          },
          {
            subtitle: '一条常见配合方式',
            steps: [
              '在私人助理里提出目标。',
              '需要长期推进时，把主任务放入项目管理。',
              '把说明文档、规范和历史资料补进知识库。',
              '把执行结果回到项目管理，把复用资料回到知识库。'
            ]
          },
          {
            subtitle: '这样分工最省事',
            bullets: [
              '有文件、预览、版本和交付，就以项目管理为主。',
              '有文档、规范、背景和长期检索，就以知识库为主。',
              '需要两者并用时，让项目管理承接当前推进，让知识库承接长期背景。'
            ]
          }
        ]
      },
      whyNotChat: {
        title: '为什么适合持续工作',
        summary:
          'Avelia 的重点，不是把回答写得更长，而是把目标、过程、执行面和结果沉淀组织进同一条工作链里。',
        sections: [
          {
            subtitle: '它更适合哪些工作方式',
            bullets: [
              '目标会连续推进几轮，而不是一句问完就结束。',
              '任务需要文件、资料、预览、素材或环境一起参与。',
              '结果需要进入项目管理、知识库或创意资产，而不是只停留在对话框里。'
            ]
          },
          {
            subtitle: '为什么这会带来更大差异',
            content:
              '真正高价值的工作，通常同时需要理解、执行、验证和沉淀。Avelia 的优势，就是让这些动作彼此连通，而不是把它们拆成几段互相断开的工具体验。'
          }
        ]
      },
      capabilityBoundaries: {
        title: '哪些能力可以立即开始',
        summary:
          '理解 Avelia 的最好方式，不是先数功能，而是先知道哪些事情可以直接开始，哪些事情需要节点、资料或权限准备好之后再推进。',
        sections: [
          {
            subtitle: '现在就可以直接开始的方向',
            bullets: [
              '在私人助理里发起目标、补充上下文、组织任务。',
              '在项目管理中承接长期会话、工作区、工具和预览。',
              '在知识库中整理资料、上传文件、编辑文本和做长期沉淀。',
              '在创意工坊中处理图片、视频和音频任务。'
            ]
          },
          {
            subtitle: '需要前置条件再推进的方向',
            bullets: [
              '桌面、Linux 和移动设备执行，需要对应节点已经接入且状态正常。',
              '远程运维、数据库和缓存相关任务，需要明确的访问条件与权限范围。',
              '资料密集型任务，需要先把关键背景放进知识库，效果才会稳定。'
            ]
          },
          {
            subtitle: '最稳妥的启动方式',
            callout: {
              tone: 'info',
              title: '先从一条简单链路验证',
              content:
                '先用私人助理把目标讲清，再决定是否进入项目管理、知识库或节点管理。这样更容易快速确认当前能力边界，也能避免一开始就把任务设计得过重。'
            }
          }
        ]
      }
    }
  },
  en: {
    defaultSection: 'platformOverview',
    pageTitle: 'Docs',
    pageSubtitle: 'Understand Avelia through real product entry points, from Private Assistant to projects, knowledge, nodes, and creative work.',
    menuTitle: 'Docs Menu',
    searchPlaceholder: 'Search Private Assistant, Project Management, Knowledge Base, Node Management...',
    searchEmpty: 'No matching content found. Try keywords closer to a real task or product entry.',
    comingSoon: 'Documentation is being updated.',
    copyButtonLabel: 'Copy Current Doc',
    copySuccessLabel: 'Copied',
    copyErrorLabel: 'Copy failed',
    copyButtonHint: 'Copy this page and hand it to a teammate, or continue the same objective inside Avelia.',
    usageTipTitle: 'Reading Tip',
    usageTipContent:
      'Start with Capabilities to decide where a task should begin. Then move to Scenarios to map that path onto your real work.',
    sourceLabel: 'Source',
    categories: [
      {
        id: 'features',
        title: 'Capabilities',
        items: [
          { id: 'platformOverview', name: 'What the Platform Is', desc: 'Understand how the main product entries connect into one work chain' },
          { id: 'gettingStarted', name: 'How to Start', desc: 'Get from first login to a real executable result faster' },
          { id: 'conversationTasks', name: 'Conversations and Tasks', desc: 'Use Private Assistant to frame goals, attach context, select skills, and track progress' },
          { id: 'projectsKnowledge', name: 'Projects and Knowledge', desc: 'Decide when work belongs in Project Management and when it belongs in the Knowledge Base' },
          { id: 'skillsNodes', name: 'Skills and Nodes', desc: 'Understand skill selection, node onboarding, and execution routing' },
          { id: 'collaborationControl', name: 'Collaboration and Control', desc: 'Choose the right flow across Private Assistant, Project Management, and Group Collaboration' }
        ]
      },
      {
        id: 'scenarios',
        title: 'Scenarios',
        items: [
          { id: 'webProjects', name: 'Web and Digital Projects', desc: 'Best for site revisions, docs sites, landing pages, and long-running digital work' },
          { id: 'creativeProduction', name: 'Creative Production', desc: 'Turn image, video, and audio work into reusable creative assets' },
          { id: 'remoteOps', name: 'Remote Operations', desc: 'Use Linux nodes for servers, deployment checks, databases, caches, and maintenance tasks' },
          { id: 'desktopExecution', name: 'Desktop Execution', desc: 'Bring local observation, app actions, and result return into one chain' },
          { id: 'mobileNodes', name: 'Mobile Node Workflows', desc: 'Let mobile devices participate in observation, execution, and result return' },
          { id: 'teamWorkflow', name: 'Team Workflows', desc: 'A controllable way to organize owners, contributors, and long-running collaboration' }
        ]
      },
      {
        id: 'faq',
        title: 'FAQ',
        items: [
          { id: 'suitableTasks', name: 'What Tasks Fit Best Right Now', desc: 'Quickly judge which goals show value fastest inside Avelia' },
          { id: 'whatIsNode', name: 'How Nodes Participate in Work', desc: 'Understand the role a node plays inside the work chain' },
          { id: 'projectKnowledgeDiff', name: 'How Projects and Knowledge Divide the Work', desc: 'See what each layer should carry' },
          { id: 'whyNotChat', name: 'Why It Fits Continuous Work', desc: 'See the practical difference from one-shot chat products' },
          { id: 'capabilityBoundaries', name: 'What You Can Start Right Away', desc: 'Know what is ready now and what depends on access, assets, or nodes' }
        ]
      }
    ],
    content: {
      platformOverview: {
        title: 'How to Build the Right Mental Model Fast',
        summary:
          'The best way to use Avelia is not to study every page first. Start in Private Assistant, then move into Project Management, the Knowledge Base, Creative Studio, or Node Management based on the task.',
        sections: [
          {
            subtitle: 'Keep this chain in mind',
            content:
              'Start with the goal, add references, then decide whether the work needs long-running project structure, creative generation, or execution inside a real environment. That is how a request becomes a reusable work chain.'
          },
          {
            subtitle: 'Recommended usage order',
            steps: [
              'Start in Private Assistant and describe the goal, background, limits, and desired outcome.',
              'If the work will continue across several rounds, move it into Project Management early so the conversation, workspace, and previews stay together.',
              'If the work depends on references, standards, or prior documents, place them in the Knowledge Base.',
              'If the work needs image, video, or audio output, move directly into Creative Studio.',
              'If the work must enter a desktop, Linux environment, or mobile device, confirm the relevant node state in Node Management.'
            ]
          },
          {
            subtitle: 'What each core entry is for',
            cards: [
              { title: 'Private Assistant', desc: 'The default starting point for clarifying goals, attaching context, selecting skills, and watching task progress.' },
              { title: 'Project Management', desc: 'The long-running container for conversations, workspace files, tools, previews, and delivery notes.' },
              { title: 'Knowledge Base', desc: 'The long-term place for references, documents, standards, and reusable context.' },
              { title: 'Creative Studio', desc: 'The place for image, video, and audio generation, retries, sharing, downloads, and asset reuse.' },
              { title: 'Node Management', desc: 'The control surface for node onboarding, live status, capability review, and runtime visibility.' },
              { title: 'Settings', desc: 'The supporting area for model, tool, and integration setup, not the best starting point for first use.' }
            ]
          },
          {
            subtitle: 'When work has actually begun',
            bullets: [
              'The system has understood the objective instead of only producing generic output.',
              'There is a visible next step, needed context, and a direction for execution.',
              'Long-running work has moved into Project Management or the Knowledge Base.',
              'If execution is needed, node state, capability fit, and permission boundaries are already clear.'
            ]
          }
        ]
      },
      gettingStarted: {
        title: 'How to Start',
        summary:
          'The most important thing on day one is not exploring every page. It is getting one real chain to run from a stated goal to a retained result.',
        sections: [
          {
            subtitle: 'A recommended first path',
            steps: [
              'After login, begin in Private Assistant instead of browsing the whole product.',
              'Write one clear message that includes the goal, background, time expectation, and desired output.',
              'Attach files, screenshots, or references early when they matter.',
              'If the work will continue across rounds, create a project and move the main thread into Project Management.',
              'If the work depends on references, standards, or prior material, organize them in the Knowledge Base.',
              'If the work needs a desktop, Linux environment, or mobile device, confirm the relevant node is ready.'
            ]
          },
          {
            subtitle: 'What the first message should include',
            cards: [
              { title: 'Goal', desc: 'What exactly needs to be done, such as revising a homepage, checking a deployment, or organizing materials.' },
              { title: 'Background', desc: 'The current project, related files, systems, team context, or preconditions.' },
              { title: 'Output', desc: 'Whether you want a page, code, a report, media output, or a reusable workflow.' },
              { title: 'Boundary', desc: 'What must not be changed, what needs confirmation, and what cannot be accessed yet.' }
            ]
          },
          {
            subtitle: 'When to switch into another area',
            bullets: [
              'Move into Project Management when the work needs files, previews, versions, or several iterations.',
              'Move into the Knowledge Base when the work needs lasting references or document organization.',
              'Move into Creative Studio when the work needs image, video, or audio generation.',
              'Check Node Management before tasks that depend on desktops, Linux, or mobile execution surfaces.',
              'Open Settings only when setup, models, or integrations need attention.'
            ]
          },
          {
            subtitle: 'The best day-one habit',
            callout: {
              tone: 'info',
              title: 'Prove the chain before increasing complexity',
              content:
                'The best first outcome is not finishing everything at once. It is confirming that the goal is understood, the task can progress, and the result can return into projects or knowledge for reuse.'
            }
          }
        ]
      },
      conversationTasks: {
        title: 'Conversations and Tasks',
        summary:
          'Private Assistant is the default entry. It does not only host dialogue. It also carries goal framing, attachments, skill selection, node preference, and task progress return.',
        sections: [
          {
            subtitle: 'A standard flow inside Private Assistant',
            steps: [
              'Create or switch to the most relevant conversation.',
              'Describe the goal and its context, then add files or references when needed.',
              'Attach skills if you know the task direction, or set a preferred node when a specific device matters.',
              'Watch task progress, processing signals, returned results, and blockers.',
              'Move the work into Project Management when it becomes a longer-running effort.'
            ]
          },
          {
            subtitle: 'The four practical controls you will feel most',
            cards: [
              { title: 'Conversation switching', desc: 'Useful for keeping separate objectives from polluting one another.' },
              { title: 'Attachments and references', desc: 'Useful for reducing repeated explanation and giving the task a stronger starting context.' },
              { title: 'Skill selection', desc: 'Useful when you know the task needs a particular capability class.' },
              { title: 'Node preference', desc: 'Useful when the task clearly belongs on a specific desktop, Linux, or mobile surface.' }
            ]
          },
          {
            subtitle: 'When to leave Private Assistant',
            bullets: [
              'Move into Project Management for long-running file work, previews, or workspace iteration.',
              'Move into the Knowledge Base for documents, references, and durable context.',
              'Move into Creative Studio for image, video, or audio work.',
              'Check Node Management when execution depends on a specific environment.'
            ]
          },
          {
            subtitle: 'What to state early to save time later',
            callout: {
              tone: 'warning',
              title: 'State acceptance criteria and permission limits up front',
              content:
                'If the task involves code edits, servers, data access, or risky actions, say early what success looks like, what the accessible scope is, and which steps require confirmation.'
            }
          }
        ]
      },
      projectsKnowledge: {
        title: 'Projects and Knowledge',
        summary:
          'Project Management keeps a real piece of work moving. The Knowledge Base keeps references and conclusions reusable over time. They often work together, but they do not carry the same responsibility.',
        sections: [
          {
            subtitle: 'When work belongs in Project Management',
            bullets: [
              'When it needs several rounds of iteration, files, previews, or visible delivery stages.',
              'When conversations, workspace files, tools, and explanations should stay in one container.',
              'When a team will keep advancing the same objective over time.'
            ]
          },
          {
            subtitle: 'What each one carries',
            cards: [
              { title: 'Project Management', desc: 'Long-running conversations, workspace files, tool setup, previews, and delivery context.' },
              { title: 'Knowledge Base', desc: 'Brand references, product docs, standards, prior conclusions, and searchable context.' },
              { title: 'Used together', desc: 'Projects help finish the work. Knowledge helps make the next cycle easier and more stable.' }
            ]
          },
          {
            subtitle: 'A recommended combined flow',
            steps: [
              'Start with the goal in Private Assistant.',
              'When the work needs continuity, move it into Project Management.',
              'Place standards, references, and supporting documents into the Knowledge Base.',
              'Keep iteration, previews, and delivery progress inside Project Management.',
              'Return reusable conclusions and materials into the Knowledge Base.'
            ]
          },
          {
            subtitle: 'A simple decision rule',
            bullets: [
              'If the question is how to keep the work moving, lean toward Project Management.',
              'If the question is what should stay reusable later, lean toward the Knowledge Base.',
              'If both matter, let Project Management carry the live work and let the Knowledge Base carry the durable context.'
            ]
          }
        ]
      },
      skillsNodes: {
        title: 'Skills and Nodes',
        summary:
          'Skills shape what the current task can call. Nodes decide whether those abilities can enter a real environment. The value appears when both stay coordinated inside the same task chain.',
        sections: [
          {
            subtitle: 'What to check first after node onboarding',
            steps: [
              'Open Node Management and confirm the node is fully bound.',
              'Check whether it is online, busy, or temporarily unavailable.',
              'Open node details and review capability lists, platform data, and recent runtime metrics.',
              'Only then return to Private Assistant or Project Management to launch environment-facing work.'
            ]
          },
          {
            subtitle: 'When to use automatic routing and when to prefer a node manually',
            cards: [
              { title: 'Automatic routing', desc: 'Best when the task is clear but not tied to a specific device.' },
              { title: 'Manual preference', desc: 'Best when you already know the work belongs on a particular Windows, Linux, or mobile node.' },
              { title: 'Check before sending', desc: 'Best when you need to confirm a node really exposes the capability your task depends on.' }
            ]
          },
          {
            subtitle: 'What each node direction fits best',
            cards: [
              { title: 'Windows nodes', desc: 'Desktop observation, application actions, workspace collaboration, and local troubleshooting.' },
              { title: 'Linux nodes', desc: 'Remote environments, command execution, deployment checks, log review, and infrastructure work.' },
              { title: 'Android nodes', desc: 'Mobile UI observation, step execution, and cross-end workflow participation.' }
            ]
          },
          {
            subtitle: 'Execution habits that improve outcomes',
            bullets: [
              'State the target system, the expected result, and the access boundary before launching environment work.',
              'For servers, databases, or caches, confirm capability fit and access conditions first.',
              'For risky operations, define what can proceed automatically and what requires explicit confirmation.'
            ]
          }
        ]
      },
      collaborationControl: {
        title: 'Collaboration and Control',
        summary:
          'The value of collaboration in Avelia is not more voices speaking at once. It is placing goals, roles, execution, and retained results on one controllable path.',
        sections: [
          {
            subtitle: 'Choosing the right collaboration surface',
            cards: [
              { title: 'Private Assistant', desc: 'Best for clarifying a goal quickly and proving the first task path.' },
              { title: 'Project Management', desc: 'Best for long-running work where conversations, files, previews, and delivery must stay together.' },
              { title: 'Group Collaboration', desc: 'Best for tasks that need a lead, contributors, discussion, and coordinated follow-through.' }
            ]
          },
          {
            subtitle: 'A safer flow for high-risk work',
            steps: [
              'Define the goal, scope, and acceptance criteria first.',
              'Decide who judges, who executes, and who reviews.',
              'Mark the steps that require confirmation before environment-facing actions begin.',
              'Return critical outcomes into Project Management or the Knowledge Base so they can be reviewed later.'
            ]
          },
          {
            subtitle: 'The kinds of work that benefit most from team progression',
            bullets: [
              'Work that naturally moves through analysis, implementation, verification, and packaging stages.',
              'Work that needs product, content, development, or operations to keep contributing across time.',
              'High-value work that needs permissions, checkpoints, or formal review.'
            ]
          },
          {
            subtitle: 'Where results should return',
            content:
              'Whether work starts in Private Assistant, Project Management, or Group Collaboration, durable outcomes should return into projects or knowledge. That is what prevents the next cycle from restarting at zero.'
          }
        ]
      },
      webProjects: {
        title: 'Web and Digital Projects',
        summary:
          'Homepage revisions, landing pages, documentation sites, and long-running digital projects work best when Private Assistant, Project Management, and the Knowledge Base are used together.',
        sections: [
          {
            subtitle: 'Typical goals',
            bullets: [
              'Copy rewrites, structure changes, and hero positioning updates.',
              'Responsive fixes, styling cleanup, and visual consistency work.',
              'Preview-driven iteration, version refinement, and delivery packaging.',
              'Documentation maintenance and brand reference reuse.'
            ]
          },
          {
            subtitle: 'A recommended task chain',
            steps: [
              'Start in Private Assistant and define the page, the design direction, and the acceptance criteria.',
              'Create a project so the main conversation, workspace, and iterations stay in Project Management.',
              'Place brand materials, old copy, screenshots, and standards into the Knowledge Base.',
              'Read files, change pages, check previews, and iterate inside the project.',
              'Retain final notes, version outcomes, and reusable language in the project or the Knowledge Base.'
            ]
          },
          {
            subtitle: 'Two habits that make this noticeably better',
            bullets: [
              'Loading brand language and historical references into the Knowledge Base before editing pages reduces re-alignment loops.',
              'Leaving previews and iteration notes inside the project makes later review far more efficient than relying on chat history alone.'
            ]
          }
        ]
      },
      creativeProduction: {
        title: 'Creative Production',
        summary:
          'Creative Studio is not only for producing one result. It is most valuable when prompts, references, parameters, and chosen outputs become reusable creative assets.',
        sections: [
          {
            subtitle: 'What it is good at',
            cards: [
              { title: 'Image work', desc: 'Brand visuals, concept images, materials, scenes, and reference-driven variation.' },
              { title: 'Video work', desc: 'Short clips, extensions, reference-image video, and iterative motion output.' },
              { title: 'Audio work', desc: 'Voice, music, sound cues, and other repeatable audio experiments.' }
            ]
          },
          {
            subtitle: 'A recommended task chain',
            steps: [
              'Define the audience, style direction, and final usage first.',
              'Open Creative Studio and choose the right output direction for image, video, or audio.',
              'Attach references when useful and keep track of the prompts and settings that work best.',
              'Retry, compare, share, and download instead of treating a single output as the endpoint.',
              'Retain the final version, naming, and reuse logic in Project Management or the Knowledge Base.'
            ]
          },
          {
            subtitle: 'How to make assets easier to reuse',
            bullets: [
              'Keep successful prompts, references, and usage notes together for future runs.',
              'When several people need the same asset set, store the chosen versions and notes in a shared project or knowledge space.',
              'When a theme will continue across time, stable naming and a consistent style baseline raise output quality later.'
            ]
          }
        ]
      },
      remoteOps: {
        title: 'Remote Operations',
        summary:
          'When a job needs servers, databases, caches, or remote project environments, Linux nodes become the most valuable execution surface. They let understanding, command execution, and retained results stay on one chain.',
        sections: [
          {
            subtitle: 'High-demand use cases',
            cards: [
              { title: 'Service inspection', desc: 'Check whether services are online, resource usage is abnormal, or logs show clear failure signals.' },
              { title: 'Deployment checks', desc: 'Verify configuration, release state, processes, ports, and critical dependencies before or after change.' },
              { title: 'Database and cache support', desc: 'Handle query support, status checks, connectivity diagnostics, and routine inspection tasks.' },
              { title: 'Logs and config review', desc: 'Compare environment configuration, inspect logs, and validate differences across remote systems.' }
            ]
          },
          {
            subtitle: 'A recommended task chain',
            steps: [
              'State the target environment, the symptom, and the expected outcome in Private Assistant or Project Management.',
              'Confirm the Linux node is online and can reach the environment with the required permissions.',
              'Prefer inspection and judgment before wider execution actions.',
              'If the investigation will continue, retain the work inside Project Management with its command chain and findings.',
              'Return root causes, recommendations, and future checklists into the Knowledge Base.'
            ]
          },
          {
            subtitle: 'What to confirm before starting',
            bullets: [
              'Whether the node can reach the target hosts, services, databases, or caches.',
              'Which commands or checks can run directly and which ones require confirmation.',
              'Whether the expected outcome is diagnosis, a recommendation, or direct action.'
            ]
          },
          {
            subtitle: 'Why this is a strong fit',
            content:
              'Remote operations often need judgment, real execution, and durable retention at the same time. Avelia fits this well because it keeps those actions on one visible path instead of splitting them across disconnected tools.'
          }
        ]
      },
      desktopExecution: {
        title: 'Desktop Execution',
        summary:
          'When work depends on local state, applications, windows, files, or screenshots, desktop execution can connect observation, action, and result return inside the same chain.',
        sections: [
          {
            subtitle: 'What it fits well',
            bullets: [
              'Local software checks and troubleshooting.',
              'Observation-heavy work around windows, files, screenshots, and application state.',
              'Repeated desktop actions and semi-automated flows.',
              'Everyday workstation support and assisted local execution.'
            ]
          },
          {
            subtitle: 'A recommended way to use it',
            steps: [
              'Describe the machine, the target software, and the desired outcome first.',
              'Separate actions that may be automated from actions that need human confirmation.',
              'Let the system inspect current state before deciding the next action.',
              'Return screenshots, conclusions, or next-step notes into Project Management or the Knowledge Base.'
            ]
          },
          {
            subtitle: 'Where the value really is',
            content:
              'The real value is not simply replacing clicks. It is letting the system observe local state, decide the next move, and return the result into a long-running work chain.'
          }
        ]
      },
      mobileNodes: {
        title: 'Mobile Node Workflows',
        summary:
          'Mobile nodes are useful when a phone or other mobile device should join the same work chain for interface observation, flow execution, and returned results.',
        sections: [
          {
            subtitle: 'The kinds of tasks that fit best',
            cards: [
              { title: 'Interface observation', desc: 'Reading current UI state, element changes, and likely next actions.' },
              { title: 'Flow execution', desc: 'Tapping, input, scrolling, swiping, and step-by-step actions inside the device.' },
              { title: 'Cross-end coordination', desc: 'Letting mobile devices work alongside desktop and Linux nodes inside one objective.' }
            ]
          },
          {
            subtitle: 'A recommended task chain',
            steps: [
              'State the mobile objective, the target app or flow, and the desired result.',
              'Confirm the mobile node is connected and exposes the needed capability set.',
              'Inspect the interface state first, then move through the required actions.',
              'Return process state and outcomes into Project Management or the Knowledge Base.'
            ]
          },
          {
            subtitle: 'The preconditions that matter',
            bullets: [
              'You know which device and which flow the task concerns.',
              'The node permissions and execution strategy are already ready for that device.',
              'There is a clear destination for screenshots, notes, or retained process outcomes.'
            ]
          }
        ]
      },
      teamWorkflow: {
        title: 'Team Workflows',
        summary:
          'When a task needs an owner, contributors, references, nodes, and staged delivery, Avelia works best as a team workflow rather than a set of disconnected chats.',
        sections: [
          {
            subtitle: 'The kinds of work that fit this well',
            cards: [
              { title: 'Release and revision collaboration', desc: 'For site rebuilds, launch pages, and content-plus-engineering delivery.' },
              { title: 'Operations and release assurance', desc: 'For deployment checks, service inspection, problem playback, and responsibility handoff.' },
              { title: 'Long-running projects', desc: 'For content systems, docs maintenance, and cross-cycle project work.' }
            ]
          },
          {
            subtitle: 'A recommended progression chain',
            steps: [
              'Have the owner define the goal, scope, timing, and acceptance criteria.',
              'Move the long-running work into Project Management and set the main conversation there.',
              'Place standards, references, and historical material into the Knowledge Base for shared context.',
              'Bring in desktop, Linux, or mobile nodes only when the task needs real execution.',
              'Return stage results into the project and reusable lessons into the Knowledge Base.'
            ]
          },
          {
            subtitle: 'Habits that make collaboration stable',
            bullets: [
              'Let the owner change goals and boundaries, but keep the execution chain focused.',
              'Return every important node or delivery outcome into Project Management instead of leaving it only in chat.',
              'Retain recurring templates, checklists, and working knowledge in the Knowledge Base.',
              'Keep confirmation points for risky actions so execution and review can remain separate.'
            ]
          }
        ]
      },
      suitableTasks: {
        title: 'What Tasks Fit Best Right Now',
        summary:
          'Avelia shows value fastest when a job needs context, execution surfaces, and retained outcomes instead of ending after one reply.',
        sections: [
          {
            subtitle: 'Start here first',
            bullets: [
              'Work that will move through several rounds, such as homepage revisions, material organization, or deployment checks.',
              'Work that needs files, previews, logs, screenshots, or references alongside the conversation.',
              'Work that needs desktops, Linux nodes, or mobile devices to enter real execution environments.',
              'Work where the process and result should stay reusable for the next cycle.'
            ]
          },
          {
            subtitle: 'The signs you will notice value early',
            bullets: [
              'You no longer need to restate the same background every time.',
              'The result becomes part of a project, knowledge asset, or creative library instead of disappearing into chat history.',
              'When execution matters, the system can work with nodes instead of only offering advice.'
            ]
          }
        ]
      },
      whatIsNode: {
        title: 'How Nodes Participate in Work',
        summary:
          'Nodes matter because they let Avelia enter desktops, Linux environments, and mobile devices as real execution surfaces instead of stopping at analysis.',
        sections: [
          {
            subtitle: 'What nodes do inside the work chain',
            bullets: [
              'They act as the bridge from the platform into real devices and environments.',
              'They report available capabilities and current state back to the platform.',
              'They execute work when called and return progress, status, and outcomes.'
            ]
          },
          {
            subtitle: 'The three user-facing benefits',
            cards: [
              { title: 'Environment access', desc: 'Work can move from knowing what to do into actually handling desktops, servers, or mobile devices.' },
              { title: 'Visible state', desc: 'You can review node status, capabilities, and runtime signals in Node Management.' },
              { title: 'Retained outcomes', desc: 'Execution results can return into projects and knowledge instead of vanishing after completion.' }
            ]
          }
        ]
      },
      projectKnowledgeDiff: {
        title: 'How Projects and Knowledge Divide the Work',
        summary:
          'Projects and knowledge often appear together, but they carry different responsibilities. Understanding the difference makes work progression and retention much smoother.',
        sections: [
          {
            subtitle: 'The shortest distinction',
            cards: [
              { title: 'Project Management', desc: 'Answers how a real piece of work keeps moving.' },
              { title: 'Knowledge Base', desc: 'Answers what should remain reusable after the work moves.' }
            ]
          },
          {
            subtitle: 'A common combined pattern',
            steps: [
              'Frame the goal in Private Assistant.',
              'Move long-running work into Project Management.',
              'Place references and standards into the Knowledge Base.',
              'Return live progress into the project and reusable conclusions into knowledge.'
            ]
          },
          {
            subtitle: 'A practical rule of thumb',
            bullets: [
              'If files, previews, versions, and delivery matter most, lead with Project Management.',
              'If references, documents, and durable context matter most, lead with the Knowledge Base.',
              'If both matter, let projects carry execution and let knowledge carry background.'
            ]
          }
        ]
      },
      whyNotChat: {
        title: 'Why It Fits Continuous Work',
        summary:
          'Avelia is not mainly about writing longer answers. Its practical strength is keeping goals, execution surfaces, process state, and retained outcomes on one chain.',
        sections: [
          {
            subtitle: 'The work styles it fits well',
            bullets: [
              'Work that moves through several rounds instead of ending in one reply.',
              'Work that needs files, references, previews, assets, or environments to participate.',
              'Work whose outcomes should become part of projects, knowledge, or reusable creative assets.'
            ]
          },
          {
            subtitle: 'Why that creates a meaningful difference',
            content:
              'High-value work usually needs understanding, execution, verification, and retention together. Avelia is designed to connect those actions instead of scattering them across separate tools.'
          }
        ]
      },
      capabilityBoundaries: {
        title: 'What You Can Start Right Away',
        summary:
          'The clearest way to understand Avelia is not by counting features. It is by knowing what can begin immediately and what depends on nodes, references, access, or setup.',
        sections: [
          {
            subtitle: 'What can start immediately',
            bullets: [
              'Framing goals, context, and task progression in Private Assistant.',
              'Running long-lived conversations, workspace work, tool setup, and previews in Project Management.',
              'Organizing references, uploading files, and editing text in the Knowledge Base.',
              'Running image, video, and audio tasks in Creative Studio.'
            ]
          },
          {
            subtitle: 'What depends on preconditions',
            bullets: [
              'Desktop, Linux, and mobile execution depend on the relevant node being connected and healthy.',
              'Remote operations, databases, and caches depend on reachable targets and clear access boundaries.',
              'Reference-heavy work becomes more stable when important background is loaded into the Knowledge Base first.'
            ]
          },
          {
            subtitle: 'The safest way to start',
            callout: {
              tone: 'info',
              title: 'Validate one simple chain first',
              content:
                'Start with a goal in Private Assistant, then decide whether the work needs Project Management, the Knowledge Base, or Node Management. That is the easiest way to verify the current capability boundary without over-designing the task.'
            }
          }
        ]
      }
    }
  }
}
